import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getOtDischargeNonVisitForm,
  updateOtDischargeNonVisitForm,
} from "../../../../../services/patientService";
import {
  FileCheckIcon,
  GraduationCapIcon,
  HeartPulseIcon,
  HomeIcon,
  LogOutIcon,
  MedicalNotesIcon,
  PillIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  OT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  AREAS_ADDRESSED_OPTIONS,
  ASSIST_STATUS_OPTIONS,
  CMS_CHECKLIST_OPTIONS,
  COGNITIVE_STATUS_OPTIONS,
  DISCHARGE_REASON_OPTIONS,
  DISCHARGE_TYPE_OPTIONS,
  DME_STATUS_OPTIONS,
  EDUCATION_METHOD_OPTIONS,
  EDUCATION_PROVIDED_OPTIONS,
  GOAL_OUTCOME_STATUS_OPTIONS,
  HOMEBOUND_NO_REASON_OPTIONS,
  INSTRUCTIONS_TO_OPTIONS,
  OT_DNV_ARRAY_KEYS,
  OVERALL_PROGRESS_OPTIONS,
  PAIN_LEVEL_OPTIONS,
  RECOMMENDED_SERVICE_OPTIONS,
  SAFETY_AWARENESS_OPTIONS,
  UNDERSTANDING_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_UNKNOWN_OPTIONS,
  emptyOtDischargeNonVisitPayload,
  mergeGoalOutcomes,
  toggleInList,
  today,
} from "./otDischargeNonVisitContent";
import {
  assertOtVisitMetaForFlag,
  buildOtVisitMetaProps,
  pickOtVisitMeta,
} from "./otVisitCommon";
import OtNotesCommonHeader from "./OtNotesCommonHeader";
import {
  normalizeOtCommonHeaderState,
  resolveOtNotesCommonHeader,
  withNormalizedOtCommonHeader,
} from "./otNotesCommonHeaderUtils";

const DISCHARGE_DATE_MAX = new Date().toISOString().slice(0, 10);
const OFFICE_FLAGS = new Set(["send_to_office", "to_be_sign"]);

const ATTESTATION_TEXT =
  "This is a non-visit discharge. I attest that this discharge summary accurately reflects the patient's status, skilled OT services provided during the episode, goal outcomes, and the plan for continued care based on available clinical information.";

const tableHeadClass =
  "bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500";

const normalizePainLevel = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 10 ? n : null;
};

const normalizeExclusive = (value, options) => {
  if (Array.isArray(value)) {
    return options.find((opt) => value.includes(opt)) || "";
  }
  const s = String(value || "").trim();
  return options.includes(s) ? s : "";
};

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const SelectField = ({
  label,
  name,
  value,
  options,
  canEdit,
  printMode,
  onChange,
  placeholder = "Select…",
}) => (
  <Field label={label}>
    {printMode ? (
      <div className={`${fieldClass} flex items-center`}>{value || "—"}</div>
    ) : (
      <select
        name={name}
        className={fieldClass}
        value={value || ""}
        disabled={!canEdit}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    )}
  </Field>
);

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

const emptyValues = () => ({
  ...emptyOtDischargeNonVisitPayload(),
  dischargeDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const OtDischargeNonVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const buildPatientPrefill = useCallback(() => {
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc = latestEpisode?.socDate || latestEpisode?.startDate || "";
    return {
      ...emptyOtDischargeNonVisitPayload(),
      socDate: soc ? formatDateForInput(soc) : "",
    };
  }, [patient]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyOtDischargeNonVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickOtVisitMeta(loadedPayload, data),
        dischargeDate:
          data.dischargeDate || loadedPayload.dischargeDate || today(),
        socDate: String(loadedPayload.socDate || base.socDate || "").trim(),
        commonHeader: normalizeOtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        dischargeType: normalizeExclusive(
          loadedPayload.dischargeType || base.dischargeType,
          DISCHARGE_TYPE_OPTIONS,
        ) || "Non-Visit",
        dischargeTypeOther: String(loadedPayload.dischargeTypeOther || "").trim(),
        dischargeReasonOther: String(
          loadedPayload.dischargeReasonOther || "",
        ).trim(),
        dischargeSummary: String(loadedPayload.dischargeSummary || "").trim(),
        supervisingTherapist: String(
          loadedPayload.supervisingTherapist || "",
        ).trim(),
        stillHomebound: normalizeExclusive(
          loadedPayload.stillHomebound,
          YES_NO_UNKNOWN_OPTIONS,
        ),
        homeboundNoReason: String(loadedPayload.homeboundNoReason || "").trim(),
        goalOutcomes: mergeGoalOutcomes(loadedPayload.goalOutcomes),
        adlStatus: normalizeExclusive(loadedPayload.adlStatus, ASSIST_STATUS_OPTIONS),
        mobilityStatus: normalizeExclusive(
          loadedPayload.mobilityStatus,
          ASSIST_STATUS_OPTIONS,
        ),
        cognitiveStatus: normalizeExclusive(
          loadedPayload.cognitiveStatus,
          COGNITIVE_STATUS_OPTIONS,
        ),
        painLevel: normalizePainLevel(loadedPayload.painLevel),
        safetyAwareness: normalizeExclusive(
          loadedPayload.safetyAwareness,
          SAFETY_AWARENESS_OPTIONS,
        ),
        overallProgress: normalizeExclusive(
          loadedPayload.overallProgress,
          OVERALL_PROGRESS_OPTIONS,
        ),
        functionalComments: String(loadedPayload.functionalComments || "").trim(),
        areasAddressedOther: String(loadedPayload.areasAddressedOther || "").trim(),
        interventionsBrief: String(loadedPayload.interventionsBrief || "").trim(),
        progressSummary: String(loadedPayload.progressSummary || "").trim(),
        educationOther: String(loadedPayload.educationOther || "").trim(),
        continuedTherapy: normalizeExclusive(
          loadedPayload.continuedTherapy,
          YES_NO_OPTIONS,
        ),
        recommendedService: normalizeExclusive(
          loadedPayload.recommendedService,
          RECOMMENDED_SERVICE_OPTIONS,
        ),
        recommendedProvider: String(loadedPayload.recommendedProvider || "").trim(),
        referralNeeded: normalizeExclusive(
          loadedPayload.referralNeeded,
          YES_NO_OPTIONS,
        ),
        followUpNotes: String(loadedPayload.followUpNotes || "").trim(),
        understandingConfirmed: normalizeExclusive(
          loadedPayload.understandingConfirmed,
          UNDERSTANDING_OPTIONS,
        ),
        instructionNotes: String(loadedPayload.instructionNotes || "").trim(),
        medicationChanges: normalizeExclusive(
          loadedPayload.medicationChanges,
          YES_NO_OPTIONS,
        ),
        medicationChangeDetails: String(
          loadedPayload.medicationChangeDetails || "",
        ).trim(),
        dmeStatus: normalizeExclusive(loadedPayload.dmeStatus, DME_STATUS_OPTIONS),
        equipmentDetails: String(loadedPayload.equipmentDetails || "").trim(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      OT_DNV_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveOtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Occupational Therapy Discharge (Non-Visit)",
            formType:
              clinicalForm.formType ||
              draft?.formType ||
              "ot_discharge_non_visit",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            dischargeDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getOtDischargeNonVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveOtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateGoalOutcome = (key, patch) => {
    setValues((prev) => ({
      ...prev,
      goalOutcomes: (prev.goalOutcomes || []).map((row) =>
        row.key === key ? { ...row, ...patch } : row,
      ),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        OT_FORM_EDITOR_ROLES,
        "View only — only OT/OTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType:
      form?.formType ||
      draft?.formType ||
      clinicalForm?.formType ||
      "ot_discharge_non_visit",
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      dischargeDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...otFields
    } = values;

    const nestedPayload = withNormalizedOtCommonHeader({
      ...otFields,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : "",
      socDate: values.socDate ? formatDateForInput(values.socDate) : "",
      goalOutcomes: mergeGoalOutcomes(values.goalOutcomes),
      painLevel: normalizePainLevel(values.painLevel),
    });

    OT_DNV_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    if (OFFICE_FLAGS.has(flag)) {
      if (!String(values.dischargeType || "").trim()) {
        toast.error("Select a discharge type.");
        return;
      }
      const hasReason =
        (Array.isArray(values.dischargeReasons) &&
          values.dischargeReasons.length > 0) ||
        Boolean(String(values.dischargeReasonOther || "").trim());
      if (!hasReason) {
        toast.error("Select a reason for discharge, or specify Other.");
        return;
      }
      if (!String(values.dischargeSummary || "").trim()) {
        toast.error("Discharge summary is required.");
        return;
      }
    }
    const visitMetaError = assertOtVisitMetaForFlag(values, flag, {
      requireTimes: false,
    });
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateOtDischargeNonVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.dischargeDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showTypeOther = values.dischargeType === "Other";
  const showHomeboundReason = values.stillHomebound === "No";
  const showContinuedTherapy = values.continuedTherapy === "Yes";
  const showMedDetails = values.medicationChanges === "Yes";
  const showDmeDetails =
    values.dmeStatus === "Equipment Provided/Recommended";
  const showAreasOther = (values.areasAddressed || []).includes("Other");
  const showEducationOther = (values.educationProvided || []).includes("Other");

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Occupational Therapy Discharge (Non-Visit)
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home Health | CMS Compliant Documentation
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Discharge Date"
          dateValue={displayDateUS(values.dischargeDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Discharge Date
                </div>
                <InputDatePicker
                  name="dischargeDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.dischargeDate) || ""}
                  onChange={(event) =>
                    setValue("dischargeDate", event.target.value)
                  }
                  max={DISCHARGE_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildOtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <OtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
          <Section
            icon={LogOutIcon}
            title="7. Discharge Information"
            className="h-full"
          >
            <Field label="Discharge Type">
              <ExclusiveChoiceGrid
                name="dischargeType"
                options={DISCHARGE_TYPE_OPTIONS}
                value={values.dischargeType || ""}
                canEdit={canEdit}
                onChange={(opt) => {
                  setValue("dischargeType", opt);
                  if (opt !== "Other") setValue("dischargeTypeOther", "");
                }}
                columns={3}
              />
            </Field>
            {showTypeOther ? (
              <input
                type="text"
                className={`${fieldClass} mt-1.5`}
                value={values.dischargeTypeOther || ""}
                disabled={!canEdit}
                placeholder="Specify other discharge type…"
                onChange={(e) => setValue("dischargeTypeOther", e.target.value)}
              />
            ) : null}
            <div className="mt-3">
              <Field label="Discharge Date">
                <DateFieldInput
                  name="sectionDischargeDate"
                  value={values.dischargeDate}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("dischargeDate", v)}
                  max={DISCHARGE_DATE_MAX}
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Reason for Discharge">
                <CheckboxGrid
                  options={DISCHARGE_REASON_OPTIONS}
                  values={values.dischargeReasons}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("dischargeReasons", opt)}
                  columns={2}
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Other">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.dischargeReasonOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other reason…"
                  onChange={(e) =>
                    setValue("dischargeReasonOther", e.target.value)
                  }
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Supervising Therapist (if applicable)">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.supervisingTherapist || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("supervisingTherapist", e.target.value)
                  }
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Discharge Summary (Brief Explanation)">
                <textarea
                  rows={4}
                  className={textareaClass}
                  value={values.dischargeSummary || ""}
                  disabled={!canEdit}
                  placeholder="Brief explanation of this non-visit discharge…"
                  onChange={(e) => setValue("dischargeSummary", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          <div className="flex flex-col gap-3 min-h-0">
            <Section
              icon={FileCheckIcon}
              title="8. CMS Discharge Requirements"
              className="h-full"
            >
              <CheckboxGrid
                options={CMS_CHECKLIST_OPTIONS}
                values={values.cmsChecklist}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("cmsChecklist", opt)}
                columns={1}
              />
            </Section>
            <Section
              icon={HomeIcon}
              title="9. Homebound Status at Discharge"
              className="h-full"
            >
              <Field label="Is the patient still homebound?">
                <ExclusiveChoiceGrid
                  name="stillHomebound"
                  options={YES_NO_UNKNOWN_OPTIONS}
                  value={values.stillHomebound || ""}
                  canEdit={canEdit}
                  onChange={(opt) => {
                    setValue("stillHomebound", opt);
                    if (opt !== "No") setValue("homeboundNoReason", "");
                  }}
                  columns={3}
                />
              </Field>
              {showHomeboundReason ? (
                <div className="mt-3">
                  <SelectField
                    label="If No, Reason"
                    name="homeboundNoReason"
                    value={values.homeboundNoReason || ""}
                    options={HOMEBOUND_NO_REASON_OPTIONS}
                    canEdit={canEdit}
                    printMode={printMode}
                    onChange={(opt) => setValue("homeboundNoReason", opt)}
                  />
                </div>
              ) : null}
            </Section>
          </div>
        </div>

        <Section icon={TargetIcon} title="10. OT Goals &amp; Outcomes Summary">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className={tableHeadClass}>
                  <th className="px-2.5 py-2 w-[18%]">Goal/Outcome</th>
                  <th className="px-2.5 py-2">Goal Description</th>
                  <th className="px-2.5 py-2 w-[22%]">Status</th>
                  <th className="px-2.5 py-2 w-[14%]">Outcome Achieved</th>
                </tr>
              </thead>
              <tbody>
                {(values.goalOutcomes || []).map((row) => (
                  <tr key={row.key} className="border-t border-slate-100">
                    <td className="px-2.5 py-1.5 font-medium text-slate-700 whitespace-nowrap">
                      {row.label}
                    </td>
                    <td className="px-1.5 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.description || ""}
                        disabled={!canEdit}
                        placeholder={
                          row.key === "other" ? "Enter goal description" : ""
                        }
                        onChange={(e) =>
                          updateGoalOutcome(row.key, {
                            description: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td className="px-1.5 py-1">
                      <select
                        className={fieldClass}
                        value={row.status || ""}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateGoalOutcome(row.key, { status: e.target.value })
                        }
                      >
                        <option value="">Select…</option>
                        {GOAL_OUTCOME_STATUS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-1.5 py-1">
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          className={`${fieldClass} pr-6`}
                          value={row.outcomePercent || ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateGoalOutcome(row.key, {
                              outcomePercent: e.target.value,
                            })
                          }
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
                          %
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section
          icon={HeartPulseIcon}
          title="11. Current Functional Status at Discharge"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <SelectField
              label="ADL Status"
              name="adlStatus"
              value={values.adlStatus || ""}
              options={ASSIST_STATUS_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(opt) => setValue("adlStatus", opt)}
            />
            <SelectField
              label="Mobility Status"
              name="mobilityStatus"
              value={values.mobilityStatus || ""}
              options={ASSIST_STATUS_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(opt) => setValue("mobilityStatus", opt)}
            />
            <SelectField
              label="Cognitive Status"
              name="cognitiveStatus"
              value={values.cognitiveStatus || ""}
              options={COGNITIVE_STATUS_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(opt) => setValue("cognitiveStatus", opt)}
            />
            <SelectField
              label="Pain Level (0–10)"
              name="painLevel"
              value={
                values.painLevel === null || values.painLevel === undefined
                  ? ""
                  : String(values.painLevel)
              }
              options={PAIN_LEVEL_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(opt) =>
                setValue("painLevel", opt === "" ? null : Number(opt))
              }
            />
            <SelectField
              label="Safety Awareness"
              name="safetyAwareness"
              value={values.safetyAwareness || ""}
              options={SAFETY_AWARENESS_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(opt) => setValue("safetyAwareness", opt)}
            />
            <SelectField
              label="Overall Progress"
              name="overallProgress"
              value={values.overallProgress || ""}
              options={OVERALL_PROGRESS_OPTIONS}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(opt) => setValue("overallProgress", opt)}
            />
          </div>
          <div className="mt-3">
            <Field label="Comments / Summary">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.functionalComments || ""}
                disabled={!canEdit}
                placeholder="Document functional status comments…"
                onChange={(e) => setValue("functionalComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section
          icon={MedicalNotesIcon}
          title="12. Clinical Summary (What Was Addressed During Episode)"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
            <div className="rounded-md border border-slate-200 p-2.5 h-full">
              <SubHeading>Primary Areas Addressed</SubHeading>
              <div className="mt-2">
                <CheckboxGrid
                  options={AREAS_ADDRESSED_OPTIONS}
                  values={values.areasAddressed}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("areasAddressed", opt)}
                  columns={2}
                />
              </div>
              {showAreasOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-2`}
                  value={values.areasAddressedOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other area…"
                  onChange={(e) =>
                    setValue("areasAddressedOther", e.target.value)
                  }
                />
              ) : null}
            </div>
            <div className="rounded-md border border-slate-200 p-2.5 h-full space-y-2">
              <Field label="Interventions Provided (Brief)">
                <textarea
                  rows={5}
                  className={textareaClass}
                  value={values.interventionsBrief || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("interventionsBrief", e.target.value)
                  }
                />
              </Field>
              <Field label="Patient Progress Summary">
                <textarea
                  rows={5}
                  className={textareaClass}
                  value={values.progressSummary || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("progressSummary", e.target.value)}
                />
              </Field>
            </div>
            <div className="rounded-md border border-slate-200 p-2.5 h-full">
              <SubHeading>Patient/Caregiver Education Provided</SubHeading>
              <div className="mt-2">
                <CheckboxGrid
                  options={EDUCATION_PROVIDED_OPTIONS}
                  values={values.educationProvided}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("educationProvided", opt)}
                  columns={1}
                />
              </div>
              {showEducationOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-2`}
                  value={values.educationOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other education…"
                  onChange={(e) => setValue("educationOther", e.target.value)}
                />
              ) : null}
            </div>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
          <Section
            icon={HomeIcon}
            title="13. Continued Care Plan"
            className="h-full"
          >
            <Field label="Does patient require continued therapy?">
              <ExclusiveChoiceGrid
                name="continuedTherapy"
                options={YES_NO_OPTIONS}
                value={values.continuedTherapy || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("continuedTherapy", opt)}
                columns={2}
              />
            </Field>
            {showContinuedTherapy ? (
              <div className="mt-3 space-y-2">
                <SelectField
                  label="If Yes, Recommended Service"
                  name="recommendedService"
                  value={values.recommendedService || ""}
                  options={RECOMMENDED_SERVICE_OPTIONS}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(opt) => setValue("recommendedService", opt)}
                />
                <Field label="Recommended Provider / Agency">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.recommendedProvider || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("recommendedProvider", e.target.value)
                    }
                  />
                </Field>
              </div>
            ) : null}
            <div className="mt-3">
              <Field label="Referral / Follow-up Needed">
                <ExclusiveChoiceGrid
                  name="referralNeeded"
                  options={YES_NO_OPTIONS}
                  value={values.referralNeeded || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("referralNeeded", opt)}
                  columns={2}
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Follow-up Instructions / Notes">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.followUpNotes || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("followUpNotes", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          <Section
            icon={GraduationCapIcon}
            title="14. Discharge Instructions Provided"
            className="h-full"
          >
            <Field label="Instructions Provided To">
              <CheckboxGrid
                options={INSTRUCTIONS_TO_OPTIONS}
                values={values.instructionsTo}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("instructionsTo", opt)}
                columns={2}
              />
            </Field>
            <div className="mt-3">
              <Field label="Method of Education">
                <CheckboxGrid
                  options={EDUCATION_METHOD_OPTIONS}
                  values={values.educationMethods}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("educationMethods", opt)}
                  columns={2}
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Understanding Confirmed">
                <ExclusiveChoiceGrid
                  name="understandingConfirmed"
                  options={UNDERSTANDING_OPTIONS}
                  value={values.understandingConfirmed || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("understandingConfirmed", opt)}
                  columns={3}
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Additional Notes">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.instructionNotes || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("instructionNotes", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          <Section
            icon={PillIcon}
            title="15. Medication &amp; Equipment"
            className="h-full"
          >
            <Field label="Any changes in medications?">
              <ExclusiveChoiceGrid
                name="medicationChanges"
                options={YES_NO_OPTIONS}
                value={values.medicationChanges || ""}
                canEdit={canEdit}
                onChange={(opt) => {
                  setValue("medicationChanges", opt);
                  if (opt !== "Yes") setValue("medicationChangeDetails", "");
                }}
                columns={2}
              />
            </Field>
            {showMedDetails ? (
              <div className="mt-3">
                <Field label="If Yes, Details">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.medicationChangeDetails || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("medicationChangeDetails", e.target.value)
                    }
                  />
                </Field>
              </div>
            ) : null}
            <div className="mt-3">
              <Field label="Durable Medical Equipment">
                <ExclusiveChoiceGrid
                  name="dmeStatus"
                  options={DME_STATUS_OPTIONS}
                  value={values.dmeStatus || ""}
                  canEdit={canEdit}
                  onChange={(opt) => {
                    setValue("dmeStatus", opt);
                    if (opt !== "Equipment Provided/Recommended") {
                      setValue("equipmentDetails", "");
                    }
                  }}
                  columns={1}
                />
              </Field>
            </div>
            {showDmeDetails ? (
              <div className="mt-3">
                <Field label="Equipment Details">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.equipmentDetails || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("equipmentDetails", e.target.value)
                    }
                  />
                </Field>
              </div>
            ) : null}
          </Section>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50/50 px-3 py-2.5">
          <p className="text-[11px] leading-relaxed text-slate-600">
            {ATTESTATION_TEXT}
          </p>
        </div>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="OT Authentication"
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

OtDischargeNonVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default OtDischargeNonVisitForm;
